/**
 * Settings (ADR-034): the safeguards on this app's roles here, and who else
 * manages or reviews them.
 *
 * Owners only. Turning a safeguard ON needs nothing more; turning one OFF
 * needs a step-up proof and emails the workspace's other owners — including
 * turning email notices off, so it cannot be silenced first. The server
 * decides which is which; this asks for proof when it says so.
 *
 * A safeguard is in force at the stricter of the workspace's setting (for
 * every app) and this app's own: this app's row can turn one ON for this app,
 * never off where the workspace has it on (`setBy` says which decided). Such
 * a safeguard is shown as set for the whole workspace, and is not offered to
 * turn off here — it would change nothing.
 *
 * While the app is not approved here (`admitted: false`) settings and
 * appointments cannot change (the server refuses); ending an appointment can.
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import type {
  AppAccessScreenDto,
  AppAccessSettingsDto,
  AppAccessSettingSourceDto,
  VaultHolderMemberDto,
} from '@/lib/api';
import { StepUpDialog, type StepUpCredentials } from '../../components/StepUpDialog';
import {
  updateAppAccessSettings,
  useAppAccessInvalidator,
  useAppointAppAccessRole,
  useEndAppAccessAppointment,
  type AppAccessScope,
} from '../../hooks/useAppAccess';
import { appAccessErrorCode, describeAppAccessError, memberLabel } from './shared';

interface SettingsSectionProps {
  scope: AppAccessScope;
  appName: string;
  screen: AppAccessScreenDto;
  members: readonly VaultHolderMemberDto[];
}

const SAFEGUARDS: ReadonlyArray<{
  key: keyof AppAccessSettingsDto;
  label: string;
  description: string;
}> = [
  {
    key: 'ownerDualControl',
    label: 'Owners also need a second person for sensitive roles',
    description:
      'Admins and appointed managers always do. Off: an owner gives a sensitive role alone, recorded as such, and the other owners are told.',
  },
  {
    key: 'ownerOnlySensitive',
    label: 'Only owners manage sensitive roles',
    description: 'Admins and appointed managers can still manage the other roles.',
  },
  {
    key: 'notifyByEmail',
    label: 'Email notices',
    description:
      'Owners are emailed when a sensitive role is given; managers when someone asks for access.',
  },
];

/** Where a safeguard's value comes from, when it is not this app's own choice. */
function sourceNote(source: AppAccessSettingSourceDto | undefined, on: boolean): string | null {
  if (!on || source === undefined || source === 'app') return null;
  return source === 'workspace'
    ? 'On for every app in this workspace — it can’t be turned off for one app.'
    : 'On by default for every app in this workspace — it can’t be turned off for one app.';
}

export function SettingsSection({ scope, appName, screen, members }: SettingsSectionProps) {
  const isOwner = screen.seat.tier === 'owner';
  const admitted = screen.admitted !== false;
  const invalidate = useAppAccessInvalidator(scope);
  const [pending, setPending] = useState<Partial<AppAccessSettingsDto> | null>(null);
  const [saving, setSaving] = useState(false);

  async function change(next: Partial<AppAccessSettingsDto>) {
    setSaving(true);
    try {
      await updateAppAccessSettings(scope, next, null);
      await invalidate();
    } catch (err) {
      if (appAccessErrorCode(err) === 'STEP_UP_REQUIRED') setPending(next);
      else
        toast({
          title: 'Not saved',
          description: describeAppAccessError(err),
          variant: 'destructive',
        });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      {pending && (
        <StepUpDialog
          open
          onOpenChange={(o) => !o && setPending(null)}
          title="Turn off a safeguard"
          description="The workspace’s other owners will be emailed that you turned it off."
          confirmLabel="Turn off"
          destructive
          onConfirm={async (creds: StepUpCredentials) => {
            await updateAppAccessSettings(scope, pending, creds);
          }}
          onSuccess={() => {
            setPending(null);
            void invalidate();
          }}
        />
      )}
      <Card>
        <CardHeader>
          <CardTitle>Safeguards for {appName}</CardTitle>
          <CardDescription>
            {!admitted
              ? `These can change once ${appName} is approved in this workspace again.`
              : isOwner
                ? 'Turning a safeguard off asks you to confirm it’s you and tells the other owners.'
                : 'Only an owner changes these.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {SAFEGUARDS.map((g) => {
            const note = sourceNote(screen.settings.setBy?.[g.key], screen.settings[g.key]);
            return (
              <div key={g.key} className="flex items-start gap-3">
                <Checkbox
                  id={`safeguard-${g.key}`}
                  checked={screen.settings[g.key]}
                  disabled={!isOwner || !admitted || saving || note !== null}
                  onCheckedChange={(v) => void change({ [g.key]: v === true })}
                />
                <div>
                  <Label htmlFor={`safeguard-${g.key}`}>{g.label}</Label>
                  <p className="text-muted-foreground">{g.description}</p>
                  {screen.settings.setBy?.[g.key] === 'app' && screen.settings[g.key] && (
                    <p className="text-xs text-muted-foreground">Set for {appName} only.</p>
                  )}
                  {note && <p className="text-xs text-muted-foreground">{note}</p>}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
      <AppointmentsCard
        scope={scope}
        screen={screen}
        members={members}
        isOwner={isOwner}
        canAppoint={isOwner && admitted}
      />
    </div>
  );
}

function AppointmentsCard({
  scope,
  screen,
  members,
  isOwner,
  canAppoint,
}: {
  scope: AppAccessScope;
  screen: AppAccessScreenDto;
  members: readonly VaultHolderMemberDto[];
  isOwner: boolean;
  /** Appointing widens who manages; not while the app is not approved here. */
  canAppoint: boolean;
}) {
  const appoint = useAppointAppAccessRole(scope);
  const end = useEndAppAccessAppointment(scope);
  const [principalId, setPrincipalId] = useState('');
  const [kind, setKind] = useState<'manager' | 'reviewer'>('manager');
  const [allApps, setAllApps] = useState(false);
  const byPrincipal = new Map(members.map((m) => [m.principalId, m]));
  // Owners and admins manage already; an appointment is for anyone else.
  const candidates = members.filter(
    (m) => m.status === 'active' && m.role !== 'owner' && m.role !== 'admin',
  );
  const onError = (title: string) => (err: unknown) =>
    toast({ title, description: describeAppAccessError(err), variant: 'destructive' });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Access managers and reviewers</CardTitle>
        <CardDescription>
          Owners and admins manage roles already. Appoint a member to manage roles, or to review
          them without changing them. Leaving the workspace ends an appointment.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {screen.appointments.length === 0 && (
          <p className="text-muted-foreground">No one is appointed.</p>
        )}
        {screen.appointments.map((a) => (
          <div key={a.appointmentId} className="flex items-center justify-between gap-2">
            <span>
              {memberLabel(byPrincipal.get(a.principalId), a.principalId)} —{' '}
              {a.kind === 'manager' ? 'access manager' : 'reviewer'}
              {a.archeId === null ? ', every app' : ''}
            </span>
            {(isOwner || a.principalId === screen.seat.principalId) && (
              <Button
                size="sm"
                variant="outline"
                disabled={end.isPending}
                onClick={() => end.mutate(a.appointmentId, { onError: onError('Not ended') })}
              >
                End
              </Button>
            )}
          </div>
        ))}
        {canAppoint && (
          <div className="grid gap-2 border-t border-border pt-3 sm:grid-cols-[1fr_auto_auto_auto] sm:items-end">
            <div className="grid gap-1.5">
              <Label htmlFor="appoint-person">Person</Label>
              <select
                id="appoint-person"
                className="h-9 rounded-md border border-input bg-background px-2"
                value={principalId}
                onChange={(e) => setPrincipalId(e.target.value)}
              >
                <option value="">Choose…</option>
                {candidates.map((m) => (
                  <option key={m.principalId} value={m.principalId}>
                    {memberLabel(m, m.principalId)}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="appoint-kind">As</Label>
              <select
                id="appoint-kind"
                className="h-9 rounded-md border border-input bg-background px-2"
                value={kind}
                onChange={(e) => setKind(e.target.value as 'manager' | 'reviewer')}
              >
                <option value="manager">Access manager</option>
                <option value="reviewer">Reviewer</option>
              </select>
            </div>
            <label className="flex items-center gap-2 pb-2">
              <Checkbox checked={allApps} onCheckedChange={(v) => setAllApps(v === true)} />
              Every app
            </label>
            <Button
              disabled={!principalId || appoint.isPending}
              onClick={() =>
                appoint.mutate(
                  { principalId, kind, allApps },
                  { onSuccess: () => setPrincipalId(''), onError: onError('Not appointed') },
                )
              }
            >
              Appoint
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
